import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';

const FAQSection = ({ title, questions }) => {
  const [openIndex, setOpenIndex] = useState(null);

  return (
    <div className="mb-12">
      <h2 className="text-2xl font-bold mb-6">{title}</h2>
      <div className="space-y-4">
        {questions.map((faq, index) => (
          <div 
            key={index}
            className="border border-gray-100 rounded-lg shadow-sm bg-white transition-all duration-200 hover:shadow-md"
          >
            <button
              className="w-full px-6 py-4 text-left flex items-center justify-between focus:outline-none"
              onClick={() => setOpenIndex(openIndex === index ? null : index)}
            >
              <span className="font-medium text-gray-900">{faq.question}</span>
              {openIndex === index ? (
                <ChevronUp className="w-5 h-5 text-gray-500" />
              ) : (
                <ChevronDown className="w-5 h-5 text-gray-500" />
              )}
            </button>
            {openIndex === index && (
              <div className="px-6 pb-4">
                <p className="text-gray-600">{faq.answer}</p>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

const FAQ = () => {
  const faqData = {
    ordering: [
      {
        question: "How do I place an order?",
        answer: "Simply browse our products, add items to your cart, and proceed to checkout. You'll need to provide shipping details and payment information to complete your purchase."
      },
      {
        question: "What payment methods do you accept?",
        answer: "We accept all major credit cards (Visa, MasterCard, American Express), PayPal, and digital wallets including Apple Pay and Google Pay."
      },
      {
        question: "Can I modify or cancel my order?",
        answer: "Orders can be modified or cancelled within 1 hour of placement. Please contact our customer service team immediately for assistance."
      },
      {
        question: "Do you offer international shipping?",
        answer: "Yes, we ship to most countries worldwide. Shipping costs and delivery times vary by location."
      }
    ],
    shipping: [
      {
        question: "How long will it take to receive my order?",
        answer: "Domestic orders typically arrive within 3-5 business days. International shipping can take 7-14 business days depending on the destination."
      },
      {
        question: "How can I track my order?",
        answer: "Once your order ships, you'll receive a tracking number via email. You can use this to monitor your delivery status on our website."
      },
      {
        question: "Do you offer free shipping?",
        answer: "Free shipping is available for domestic orders over $50. International orders and orders below this threshold will incur shipping charges."
      }
    ],
    returns: [
      {
        question: "What is your return policy?",
        answer: "We offer a 30-day return policy for most items. Products must be unused and in their original packaging."
      },
      {
        question: "How do I initiate a return?",
        answer: "Log into your account, go to your orders, and select 'Return Item'. Follow the prompts to generate a return shipping label."
      },
      {
        question: "When will I receive my refund?",
        answer: "Refunds are processed within 3-5 business days after we receive your return. The funds may take additional time to appear in your account."
      }
    ],
    account: [
      {
        question: "How do I create an account?",
        answer: "Click the 'Sign Up' button in the top right corner and fill out the registration form with your email and password."
      },
      {
        question: "How can I reset my password?",
        answer: "Click 'Forgot Password' on the login page and follow the instructions sent to your email to reset your password."
      },
      {
        question: "Can I use multiple addresses?",
        answer: "Yes, you can save multiple shipping addresses in your account settings for convenient checkout."
      }
    ]
  };

  return (
    <div className="w-full bg-gray-50">
      {/* Header Banner */}
      <div className="relative w-full h-40">
        <img 
          src="https://images.pexels.com/photos/4033112/pexels-photo-4033112.jpeg?auto=compress&cs=tinysrgb&w=2070" 
          alt="Fresh vegetables banner"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-20">
          <h1 className="text-4xl font-bold text-white">Frequently Asked Questions</h1>
        </div>
      </div>

      {/* Search Bar */}
     

      {/* Main Content */}
      <div className="max-w-3xl mx-auto px-4 py-12">
        <div className="text-center mb-12">
          <p className="text-gray-600 text-lg">
            Find answers to common questions about our products, services, and policies.
            Can't find what you're looking for? Contact our support team.
          </p>
        </div>

        <FAQSection title="Ordering & Payment" questions={faqData.ordering} />
        <FAQSection title="Shipping & Delivery" questions={faqData.shipping} />
        <FAQSection title="Returns & Refunds" questions={faqData.returns} />
        <FAQSection title="Account & Security" questions={faqData.account} />

        {/* Still Need Help Section */}
        <div className="mt-16 text-center bg-white rounded-xl p-8 shadow-sm border border-gray-100">
          <h2 className="text-2xl font-bold mb-4">Still Need Help?</h2>
          <p className="text-gray-600 mb-6">
            Our customer support team is here to assist you with any questions or concerns.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/contact"
              className="inline-flex items-center justify-center px-6 py-3 rounded-lg bg-gray-900 text-white font-medium hover:bg-gray-800 transition-colors"
            >
              Contact Support
            </a>
            <a
              href="mailto:info@ecomify.com"
              className="inline-flex items-center justify-center px-6 py-3 rounded-lg border border-gray-300 text-gray-700 font-medium hover:bg-gray-50 transition-colors"
            >
              Email Us
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FAQ;